import msal from './lib/msal.js'
import redux from './redux'
import React from 'react'
import ReactDOM from 'react-dom'
import { MuiThemeProvider, createTheme } from '@material-ui/core/styles'
import { Provider } from 'react-redux'
import './index.css'
import App from './App.js'
import { onReady, printDiagnostics } from './lib/office.js'
import storage from './redux/storage.js'
import { isValidToken } from './util.js'

// MSAL's ssoSilent works by opening a hidden iframe that loads this page.
// If we're in that iframe (detected by being nested + having an auth response
// in the URL hash), we must not run any app startup — just let MSAL handle it.
const isMsalHiddenIframe = window.self !== window.top &&
  /[#&](code|error|state)=/.test(window.location.hash)

if (isMsalHiddenIframe) {
  msal.handleRedirectPromise().catch(() => {})
  // Stop here — this is MSAL's hidden iframe, not a real app context
} else {
  startApp()
}

// https://material-ui.com/customization/themes
const theme = createTheme({
  tooltip: {
    color: '#eeffff',
    backgroundColor: '#d57b00',
    fontSize: '16px',
    fontWeight: 'lighter',
    padding: '5px 15px 5px 15px',
    maxWidth: '600px',
    borderRadius: '3px',
    userSelect: 'text'
  },
  // https://v4.mui.com/customization/globals/#css
  overrides: {
    MuiSvgIcon: {
      root: {
        // Need smaller icons for toolbar
        fontSize: '1.3rem'
      }
    }
  }
})

function onValidToken ({ idToken = '', token = '' }) {
  onReady(() => {
    printDiagnostics()

    ReactDOM.render(
      <React.StrictMode>
        <Provider store={redux.store}>
          <MuiThemeProvider theme={theme}>
            <App idToken={idToken} token={token} />
          </MuiThemeProvider>
        </Provider>
      </React.StrictMode>,
      document.getElementById('root')
    )
  })
}

function redirectLogin () {
  if (window.self !== window.top) {
    // iframe (Office Online / O365 web) — use popup
    console.info('initiating loginPopup (O365 web context)')
    msal.loginPopup().then((tokenResponse) => {
      onValidToken({ idToken: tokenResponse.idToken })
    }).catch((err) => {
      console.error('msal.loginPopup failed', err)
    })
  } else {
    // Desktop Office add-in — use redirect
    console.info('initiating loginRedirect (desktop context)')
    msal.loginRedirect().catch((err) => {
      console.error('msal.loginRedirect failed', err)
    })
  }
}

function startApp () {
  const { token, email } = redux.store.getState().login.userData
  const loginHint = email

  // handleRedirectPromise must complete before any other MSAL call is made.
  // Chaining all startup logic here avoids an interaction_in_progress race
  // where ssoSilent fires before the redirect lock is cleared from sessionStorage.
  msal.handleRedirectPromise().then((tokenResponse) => {
    if (tokenResponse !== null) {
      // Returning from a loginRedirect — use the token and stop here.
      console.info('got id token from redirect method')
      onValidToken({ idToken: tokenResponse.idToken })
      return
    }

    if (isValidToken(token)) {
      console.info('found cached valid token')
      onValidToken({ token })
    } else if (typeof loginHint === 'string') {
      console.info('msal.ssoSilent() with login hint', loginHint)
      msal.ssoSilent({ loginHint }).then(result => {
        onValidToken({ idToken: result.idToken })
      }).catch((err) => {
        console.error('msal.ssoSilent failed', err)
        storage.deleteUserData()
        redirectLogin()
      })
    } else {
      redirectLogin()
    }
  }).catch((err) => {
    // TODO this is a fatal error -> render Error page with details
    console.error('msal.handleRedirectPromise failed', err)
  })
}
